import type {AnyAction, Dispatch} from 'redux';
import type {ThunkDispatch} from 'redux-thunk';
import documentCategoryListActions from 'src/modules/documentCategory/list/documentCategoryListActions';
import employeeCategoryListActions from 'src/modules/employeeCategory/list/employeeCategoryListActions';
import pendienteListSelectors from 'src/modules/pendiente/list/pendienteListSelectors';
import absenceListActions from 'src/modules/absence/list/absenceListActions';
import activoListActions from 'src/modules/activo/list/activoListActions';
import categoriaListActions from 'src/modules/categoria/list/categoriaListActions';
import centroCostoListActions from 'src/modules/centroCosto/list/centroCostoListActions';
import clasificacionTerceroListActions from 'src/modules/clasificacionTercero/list/clasificacionTerceroListActions';
import comunicacionInternaListActions from 'src/modules/comunicacionInterna/list/comunicacionInternaListActions';
import dataListActions from 'src/modules/data/list/dataListActions';
import dataLakeListActions from 'src/modules/dataLake/list/dataLakeListActions';
import descRegistroListActions from 'src/modules/descRegistro/list/descRegistroListActions';
import divisionListActions from 'src/modules/division/list/divisionListActions';
import firmaListActions from 'src/modules/firma/list/firmaListActions';
import gestorDocumentalListActions from 'src/modules/gestorDocumental/list/gestorDocumentalListActions';
import historialInventarioListActions from 'src/modules/historialInventario/list/historialInventarioListActions';
import incidentListActions from 'src/modules/incident/list/incidentListActions';
import inventarioListActions from 'src/modules/inventario/list/inventarioListActions';
import mensajeInternoListActions from 'src/modules/mensajeInterno/list/mensajeInternoListActions';
import modeloFormularioListActions from 'src/modules/modeloFormulario/list/modeloFormularioListActions';
import modeloTareaListActions from 'src/modules/modeloTarea/list/modeloTareaListActions';
import notificacionListActions from 'src/modules/notificacion/list/notificacionListActions';
import pendienteListActions from 'src/modules/pendiente/list/pendienteListActions';
import perfilEmpleadoListActions from 'src/modules/perfilEmpleado/list/perfilEmpleadoListActions';
import planificacionListActions from 'src/modules/planificacion/list/planificacionListActions';
import planificadorFormularioListActions from 'src/modules/planificadorFormulario/list/planificadorFormularioListActions';
import planificadorTareaListActions from 'src/modules/planificadorTarea/list/planificadorTareaListActions';
import planificadorTareaCustomListActions from 'src/modules/planificadorTareaCustom/list/planificadorTareaCustomListActions';
import planningListActions from 'src/modules/planning/list/planningListActions';
import presupuestoListActions from 'src/modules/presupuesto/list/presupuestoListActions';
import projectListActions from 'src/modules/project/list/projectListActions';
import recursoListActions from 'src/modules/recurso/list/recursoListActions';
import registroListActions from 'src/modules/registro/list/registroListActions';
import subTareaListActions from 'src/modules/subTarea/list/subTareaListActions';
import taskFormListActions from 'src/modules/taskForm/list/taskFormListActions';
import tareaListActions from 'src/modules/tarea/list/tareaListActions';
import tareaListActions2 from 'src/modules/tarea/list/tareaListActions2';
import tareaListIncidentActions from 'src/modules/tarea/list/tareaListIncidentActions';
import temaComunicacionListActions from 'src/modules/temaComunicacion/list/temaComunicacionListActions';
import terceroListActions from 'src/modules/tercero/list/terceroListActions';
import tipoActivoListActions from 'src/modules/tipoActivo/list/tipoActivoListActions';
import ubicacionListActions from 'src/modules/ubicacion/list/ubicacionListActions';

/** `redux-thunk`: los list actions exponen thunks async, no plain `AnyAction`. */
type DivisionScopeDispatch = ThunkDispatch<unknown, unknown, AnyAction> & Dispatch<AnyAction>;

function primarySegment(pathname: string): string {
  const parts = pathname.replace(/^\/+/, '').split('/').filter(Boolean);
  return parts[0] || '';
}

function refetchIncidentStack(dispatch: DivisionScopeDispatch) {
  dispatch(incidentListActions.doFetchCurrentFilter());
  dispatch(tareaListIncidentActions.doFetchCurrentFilter());
  dispatch(firmaListActions.doFetchCurrentFilter());
}

function refetchTareaLists(dispatch: DivisionScopeDispatch) {
  dispatch(tareaListActions.doFetchCurrentFilter());
  dispatch(tareaListActions2.doFetchCurrentFilter());
}

type GetState = () => {pendiente?: {list?: unknown}};

/**
 * Tras cambiar la división activa, re-ejecuta el listado Redux del módulo
 * que coincide con la ruta actual (`connected-react-router`).
 * Pantallas que gestionan su propia data (p. ej. access-admin) se omiten aquí.
 */
export function divisionScopeRefetchForPathname(
  dispatch: DivisionScopeDispatch,
  getState: GetState,
  pathname: string,
) {
  const path = pathname || '';
  if (/^\/tenant\/[^/]+\/(access-admin|divisions|custom-roles)(\/|$)/.test(path)) {
    return;
  }

  const seg = primarySegment(path);

  const bySegment: Record<string, () => void> = {
    incidente: () => refetchIncidentStack(dispatch),
    contractors: () => refetchIncidentStack(dispatch),
    tarea: () => refetchTareaLists(dispatch),
    'reporte-detenciones': () => refetchTareaLists(dispatch),
    'reporte-calidad-phsv': () => refetchTareaLists(dispatch),
    'reporte-calidad-operacional': () => refetchTareaLists(dispatch),
    kanban: () => refetchTareaLists(dispatch),
    scheduler: () => refetchTareaLists(dispatch),
    activo: () => dispatch(activoListActions.doFetchCurrentFilter()),
    pendiente: () =>
      dispatch((d, gs) => {
        const filter = pendienteListSelectors.selectFilter(gs());
        const rawFilter = pendienteListSelectors.selectRawFilter(gs());
        d(pendienteListActions.doFetchGroups(filter, rawFilter));
      }),
    firma: () => dispatch(firmaListActions.doFetchCurrentFilter()),
    categoria: () => dispatch(categoriaListActions.doFetchCurrentFilter()),
    'comunicacion-interna': () => dispatch(comunicacionInternaListActions.doFetchCurrentFilter()),
    'tema-comunicacion': () => dispatch(temaComunicacionListActions.doFetchCurrentFilter()),
    'mensaje-interno': () => dispatch(mensajeInternoListActions.doFetchCurrentFilter()),
    notificacion: () => dispatch(notificacionListActions.doFetchCurrentFilter()),
    'modelo-formulario': () => dispatch(modeloFormularioListActions.doFetchCurrentFilter()),
    'modelo-tarea': () => dispatch(modeloTareaListActions.doFetchCurrentFilter()),
    'sub-tarea': () => dispatch(subTareaListActions.doFetchCurrentFilter()),
    'planificador-formulario': () => dispatch(planificadorFormularioListActions.doFetchCurrentFilter()),
    'planificador-tarea': () => {
      dispatch(planificadorTareaListActions.doFetchCurrentFilter());
      dispatch(planificadorTareaCustomListActions.doFetchCurrentFilter());
    },
    planificacion: () => dispatch(planificacionListActions.doFetchCurrentFilter()),
    planning: () => dispatch(planningListActions.doFetchCurrentFilter()),
    ubicacion: () => dispatch(ubicacionListActions.doFetchCurrentFilter()),
    division: () => dispatch(divisionListActions.doFetchCurrentFilter()),
    'perfil-empleado': () => dispatch(perfilEmpleadoListActions.doFetchCurrentFilter()),
    'gestor-documental': () => dispatch(gestorDocumentalListActions.doFetchCurrentFilter()),
    'document-category': () => dispatch(documentCategoryListActions.doFetchCurrentFilter()),
    project: () => dispatch(projectListActions.doFetchCurrentFilter()),
    'employee-category': () => dispatch(employeeCategoryListActions.doFetchCurrentFilter()),
    registro: () => dispatch(registroListActions.doFetchCurrentFilter()),
    'desc-registro': () => dispatch(descRegistroListActions.doFetchCurrentFilter()),
    ausencia: () => dispatch(absenceListActions.doFetchCurrentFilter()),
    formularios: () => dispatch(taskFormListActions.doFetchCurrentFilter()),
    data: () => dispatch(dataListActions.doFetchCurrentFilter()),
    'data-lake': () => dispatch(dataLakeListActions.doFetchCurrentFilter()),
    'historial-inventario': () => dispatch(historialInventarioListActions.doFetchCurrentFilter()),
    inventario: () => dispatch(inventarioListActions.doFetchCurrentFilter()),
    tercero: () => dispatch(terceroListActions.doFetchCurrentFilter()),
    'clasificacion-tercero': () => dispatch(clasificacionTerceroListActions.doFetchCurrentFilter()),
    'centro-costo': () => dispatch(centroCostoListActions.doFetchCurrentFilter()),
    presupuesto: () => dispatch(presupuestoListActions.doFetchCurrentFilter()),
    recurso: () => dispatch(recursoListActions.doFetchCurrentFilter()),
    'tipo-activo': () => dispatch(tipoActivoListActions.doFetchCurrentFilter()),
  };

  const run = bySegment[seg];
  if (run) {
    run();
  }
}
